import { useRef, useState } from 'react'

import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import type { PendingToolCall } from '../core/engine/types'
import { theme } from '@/Atomic/assets/theme'

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.space.m};
  padding: ${theme.space.m} ${theme.space.l};
  background: ${theme.colors.background.default};
  border: 1px solid ${theme.colors.border.stroke};
  border-radius: ${theme.radii.md};
  margin: ${theme.space.xs} 0;
`

const CardTitle = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${theme.colors.text.primary};
`

const Row = styled.div`
  display: flex;
  gap: ${theme.space.s};
  font-size: 13px;
  flex-wrap: wrap;
  align-items: baseline;
`

const RowLabel = styled.span`
  color: ${theme.colors.text.secondary};
  flex-shrink: 0;
  min-width: 100px;
  max-width: 140px;
`

const RowValue = styled.span`
  color: ${theme.colors.text.primary};
  font-weight: 500;
  word-break: break-word;
  flex: 1;
  min-width: 0;
`

const Actions = styled.div`
  display: flex;
  gap: ${theme.space.s};
  justify-content: flex-end;
  flex-wrap: wrap;
`

const Btn = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: ${theme.space.xs} ${theme.space.m};
  border-radius: ${theme.radii.sm};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid
    ${({ variant }) =>
      variant === 'primary'
        ? theme.colors.primary.main
        : theme.colors.border.stroke};
  background: ${({ variant }) =>
    variant === 'primary' ? theme.colors.primary.main : 'transparent'};
  color: ${({ variant }) =>
    variant === 'primary' ? '#fff' : theme.colors.text.primary};
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover:not(:disabled) {
    background: ${({ variant }) =>
      variant === 'primary'
        ? theme.colors.primary.hover
        : theme.colors.background.hover};
    border-color: ${({ variant }) =>
      variant === 'primary'
        ? theme.colors.primary.hover
        : theme.colors.border.active};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

interface ConfirmationCardProps {
  pending: Extract<PendingToolCall, { type: 'confirm' }>
  /** Called with true (confirm) or false (cancel). */
  onSubmit: (result: boolean) => void
}

/**
 * ConfirmationCard — read-only summary with Cancel and Confirm.
 * Cancel resolves false so the tool returns a "Cancelled" message.
 * The user can also just type in the input to implicitly cancel and
 * start a new turn without clicking Cancel first.
 * Single-fire guard: buttons disabled after first click.
 */
export function ConfirmationCard({ pending, onSubmit }: ConfirmationCardProps) {
  const { t } = useTranslation()
  const firedRef = useRef(false)
  const [fired, setFired] = useState(false)

  function handleConfirm() {
    if (firedRef.current) return
    firedRef.current = true
    setFired(true)
    onSubmit(true)
  }

  function handleCancel() {
    if (firedRef.current) return
    firedRef.current = true
    setFired(true)
    onSubmit(false)
  }

  return (
    <Card>
      <CardTitle>{pending.spec.title}</CardTitle>
      {pending.spec.rows.map((row, i) => (
        <Row key={`${row.label}-${i}`}>
          <RowLabel>{row.label}</RowLabel>
          <RowValue>{row.value}</RowValue>
        </Row>
      ))}
      <Actions>
        <Btn
          variant="secondary"
          type="button"
          onClick={handleCancel}
          disabled={fired}
        >
          {t('components.aiChatBot.write.previewCard.cancelLabel')}
        </Btn>
        <Btn
          variant="primary"
          type="button"
          onClick={handleConfirm}
          disabled={fired}
        >
          {t('components.aiChatBot.write.previewCard.confirmLabel')}
        </Btn>
      </Actions>
    </Card>
  )
}
